/**
 * This Component is intentionally light. It exists separately
 * to ease server side rendering.
 */
import React, {useEffect, useMemo, useState} from 'react';
import App from './App';
import styled, {ThemeProvider} from 'styled-components';
import {getTheme} from '@ey/styled-theme';
import {useIsDrawerMode} from '../util/Ares/customHooks';
import {useUserPreferences} from '../util/customHooks';

export default function Routes() {
	const [theme, setTheme] = useState(undefined);

	const userPreferences = useUserPreferences();
	const isDrawerMode = useIsDrawerMode();

	let pathName = window.location.pathname;
	pathName = pathName.startsWith('/') ? pathName : '/' + pathName;

	const isPrintScreen = pathName?.toLowerCase().includes('print');

	useEffect(() => {
		if (userPreferences && userPreferences.isPreferencesLoaded === true) {
			if (userPreferences.userPreferencesJson !== null && userPreferences.userPreferencesJson !== undefined) {
				let preferences = JSON.parse(userPreferences.userPreferencesJson);
				setTheme(!isPrintScreen ? preferences.theme ?? 'light' : 'light');

				// If Print Screen always set theme to light.
				document
					.getElementsByTagName('body')[0]
					.setAttribute('motif-theme', !isPrintScreen ? preferences.theme ?? 'light' : 'light');
				document
					.getElementsByTagName('body')[0]
					.setAttribute(
						'class',
						!isPrintScreen
							? preferences?.theme === 'light'
								? 'motif-theme-light'
								: 'motif-theme-dark'
							: 'motif-theme-light'
					);
			}
		}
	}, [userPreferences]);

	useEffect(() => {
		setTheme('light');
		document.getElementsByTagName('body')[0].setAttribute('motif-theme', 'light');
		document.getElementsByTagName('body')[0].setAttribute('class', 'motif-theme-light');
	}, []);

	/**
	 * To configure a new route, add a new element with
	 *   - show: Function that takes the path as input, and returns true if the component declared in the render prop should be shown
	 *   - render: JSX fragment (component) to render if show returns true
	 */
	const componentMapping = useMemo(() => {
		return [
			// Print view (Kronos)
			{
				show: (routePath) => routePath.indexOf('/canvasform/print') >= 0,
				render: (
					<ThemeProvider theme={getTheme()}>
						<StyledLandingContainer role="main" className="rootWrapper">
							<App />
						</StyledLandingContainer>
					</ThemeProvider>
				)
			},
			// Print view (FIT)
			{
				show: (routePath) => routePath.indexOf('/guidedworkflow/print') >= 0,
				render: (
					<ThemeProvider theme={getTheme()}>
						<StyledLandingContainer role="main" className="rootWrapper">
							<App />
						</StyledLandingContainer>
					</ThemeProvider>
				)
			},
			{
				show: (routePath) => routePath.indexOf('/guidedworkflowv2/print') >= 0,
				render: (
					<ThemeProvider theme={getTheme()}>
						<StyledLandingContainer role="main" className="rootWrapper">
							<App />
						</StyledLandingContainer>
					</ThemeProvider>
				)
			},
			// FIT v2 container
			{
				show: (routePath) => routePath.startsWith('/guidedworkflowv2'),
				render: (
					<ThemeProvider theme={getTheme(theme && theme.toLowerCase() === 'dark')}>
						<StyledLandingContainer role="main" className={`rootWrapper ${isDrawerMode && 'DrawerRootWrapper'}`}>
							<App hasNavigation hasError />
						</StyledLandingContainer>
					</ThemeProvider>
				)
			},
			// FIT v1 container
			{
				show: (routePath) => routePath.startsWith('/guidedworkflow'),
				render: (
					<ThemeProvider theme={getTheme(theme && theme.toLowerCase() === 'dark')}>
						<StyledLandingContainer role="main" className={`rootWrapper ${isDrawerMode && 'DrawerRootWrapper'}`}>
							<App hasNavigation hasError />
						</StyledLandingContainer>
					</ThemeProvider>
				)
			},
			// Standalone
			{
				show: (routePath) => routePath.startsWith('/standalone'),
				render: (
					<ThemeProvider theme={getTheme(theme && theme.toLowerCase() === 'dark')}>
						<StyledLandingContainer role="main" className={`rootWrapper ${isDrawerMode && 'DrawerRootWrapper'}`}>
							<App />
						</StyledLandingContainer>
					</ThemeProvider>
				)
			}
		];
	}, [isDrawerMode, theme]);

	const routeContent = useMemo(() => {
		return componentMapping.find((mapping) => mapping.show(pathName));
	}, [componentMapping]);

	if (routeContent) {
		return routeContent.render;
	}

	// Canvas Forms (Kronos) -- Default view
	return (
		<ThemeProvider theme={getTheme(theme && theme.toLowerCase() === 'dark')}>
			<StyledLandingContainer role="main" className={`rootWrapper ${isDrawerMode && 'DrawerRootWrapper'}`}>
				<App hasNavigation hasError />
			</StyledLandingContainer>
		</ThemeProvider>
	);
}

const StyledLandingContainer = styled.section`
	&.rootWrapper {
		.container-fluid {
			margin: 0;
		}
	}
`;
